import { ThunkAction } from 'redux-thunk';
import isNil from 'lodash/isNil';
import omit from 'lodash/omit';

import { hideLoading, showLoading } from 'redux-store/reducers/loading-reducer';
import { danger } from 'redux-store/reducers/common-reducer';
import { receiveBrief } from 'modules/campaigns/reducers/campaignBrief';
import { fetchInfluencerResponseTime } from 'modules/influencers/reducers/influencerResponseTime';

import { API, get, patch, put, request } from 'utils/api';
import { logError } from 'utils/log';
import { Querystring } from 'utils/url';
import { __t, applyTranslations } from 'utils/language';
import { isCcmCampaignTimeline } from '../utils/ccm-timeline';

applyTranslations(require('../ccm-campaign-common.i18n.json'));

import type {
  Campaign,
  Dispatch,
  GetState,
  MixedSelectDropdownItem,
  PaginationLinks,
  PaginationMeta,
  ThunkAction as ThunkActionCollabary
} from 'types';
import type {
  CcmCampaignAction,
  CcmCampaignAssetsLoadedAction,
  CcmCampaignFormDetails,
  CcmCampaignMetadata,
  CcmCampaignTimeline,
  CcmCampaignTimelineGeneratedAction
} from '../types';
import type {
  CcmOfferResult,
  CcmOfferScreenState
} from '../components/offers/types';
import type {
  CcmCampaignReportingOutfitPerformance
} from '../types/CcmCampaignReporting.type';

type AsyncAction = ThunkAction<Promise<Campaign>, {}, {}, CcmCampaignAction>;

const { CCM } = API.END_POINTS;
const MSG_KEY = 'ccm_campaign_common';
const PAGINATION_LIMIT = 50;

export const createCampaign = (
  campaign: Campaign
): CcmCampaignAction => ({
  type: 'ccm-campaign-create',
  payload: {
    campaign
  }
});

export const updateCampaign = (
  campaign: Campaign
): CcmCampaignAction => ({
  type: 'ccm-campaign-update',
  payload: {
    campaign
  }
});

export const approveCampaign = (
  campaign: Campaign
): CcmCampaignAction => ({
  type: 'ccm-campaign-approve',
  payload: {
    campaign
  }
});

export const receivePool = (
  pool: Array<CcmOfferResult>
): CcmCampaignAction => ({
  type: 'ccm-campaign-pool-get',
  payload: {
    pool
  }
});

export const receiveOutfitPerformance = (
  outfitPerformance: CcmCampaignReportingOutfitPerformance
): CcmCampaignAction => ({
  type: 'ccm-campaign-outfit-performance-get',
  payload: {
    outfitPerformance
  }
});

export const updateOutfitPerformanceCountries = (
  outfitPerformanceCountries: Array<MixedSelectDropdownItem>
): CcmCampaignAction => ({
  type: 'ccm-campaign-outfit-performance-countries-update',
  payload: {
    outfitPerformanceCountries
  }
});

export const flushCcmPool = (): CcmCampaignAction => ({
  type: 'ccm-campaign-pool-flush'
});

const screenStateUpdated = (
  updatedScreenState: CcmOfferScreenState
): CcmCampaignAction => ({
  type: 'ccm-campaign-screen-state-updated',
  payload: {
    screenState: updatedScreenState
  }
});

const paginationUpdated = (
  meta: PaginationMeta,
  links: PaginationLinks
): CcmCampaignAction => ({
  type: 'ccm-campaign-pagination-updated',
  payload: {
    meta,
    links
  }
});

export const ccmCampaignTimelineGenerated = (
  timeline: CcmCampaignTimeline
): CcmCampaignTimelineGeneratedAction => ({
  type: 'ccm-campaign-timeline-generated',
  payload: {
    timeline
  }
});

export const flushCcmCampaignTimeline = (): CcmCampaignAction => ({
  type: 'ccm-campaign-timeline-flush'
});

export const updateScreenState = (
  screenState: CcmOfferScreenState
): ThunkActionCollabary => (
  async (dispatch: Dispatch) => {
    dispatch(screenStateUpdated(screenState));
  }
);

const updatePagination = (
  meta: PaginationMeta,
  links: PaginationLinks
): ThunkActionCollabary => (
  async (dispatch: Dispatch) => {
    dispatch(paginationUpdated(meta, links));
  }
);

const getUpdatedScreenState = (
  meta: PaginationMeta,
  links: PaginationLinks
): CcmOfferScreenState => ({
  page: meta.currentPage || 0,
  totalItems: Number(meta.itemsTotal || 0),
  links,
  meta: {
    ...meta,
    itemsTotal: Number(meta.itemsTotal || 0)
  }
});

const ccmCampaignAssetsLoaded = (template: string): CcmCampaignAssetsLoadedAction => ({
  type: 'ccm-campaign-assets-loaded',
  payload: {
    ccmLegalRequirementsTemplate: template
  }
});

export const loadCcmAssets = () => async (dispatch: Dispatch) => {
  try {
    const url: string = API.END_POINTS.CCM.ASSETS.getCcmLegalRequirements();
    const template = (await get(
      url,
      { headers: { Accept: 'text/plain' } },
      { suppressAuth: true }
    )).data;

    if (typeof template !== 'string') {
      throw new Error('CCM Assets / Wrong asset type returned');
    }

    dispatch(ccmCampaignAssetsLoaded(template));
  } catch (error) {
    logError(error);
  }
};

export const saveCcmCampaign = (
  brandProfileId: number,
  formDetails: CcmCampaignFormDetails,
  shouldUpdate: boolean
): AsyncAction => (
  async (dispatch: Dispatch) => {
    const canUpdate = shouldUpdate && !isNil(formDetails.id);
    const method = canUpdate ? 'PUT' : 'POST';

    const url = canUpdate
      ? CCM.updateCampaign(formDetails.id)
      : CCM.createCampaign(brandProfileId);

    dispatch(showLoading(url));

    try {
      const result = await request(method, url, {
        data: {
          ...omit(formDetails, ['teamMembers']),
          managersIds: formDetails.teamMembers
            .filter(tm => tm.isSelected)
            .map(tm => Number(tm.identifier)),
          campaignType: 'ccm'
        }
      });

      if (canUpdate) {
        dispatch(updateCampaign(result.data));
      } else {
        dispatch(createCampaign(result.data));
      }

      dispatch(hideLoading(url));

      return result.data;
    } catch (error) {
      const details = error?.response?.data?.detail;
      const message = details ?? __t(`${MSG_KEY}.errors.generic`);

      logError(error);

      dispatch(hideLoading(url));
      dispatch(danger(message));

      return null;
    }
  }
);

export const approveCcmCampaign = (
  campaignId: number
): ThunkActionCollabary => (
  async (dispatch: Dispatch) => {
    try {
      const url = CCM.approveCampaign(campaignId);
      const result = await put(url);

      dispatch(approveCampaign(result.data));
      dispatch(receiveBrief(result.data));

      return result.data;
    } catch (error) {
      const details = error?.response?.data?.detail;
      const message = details ?? __t(`${MSG_KEY}.errors.generic`);

      logError(error);
      dispatch(danger(message));

      return null;
    }
  }
);

export const fetchCcmPool = (
  campaignId: number,
  currentPage: number = 1
): AsyncAction => (
  async (dispatch: Dispatch) => {
    const params = {
      page: currentPage,
      items_per_page: PAGINATION_LIMIT
    };

    const queryString = Querystring.objectToString(params);

    try {
      const url = CCM.getPool(campaignId, queryString);
      const response = await get(url);
      const results = response.data?.data ?? [];
      const meta = response.data?.meta ?? {};
      const links = response.data?.links ?? {};
      const updatedScreenState = getUpdatedScreenState(meta, links);

      if (isNil(results)) {
        throw new Error(`No campaign pool was returned for campaign id: ${campaignId}`);
      }

      dispatch(updatePagination(meta, links));
      dispatch(updateScreenState(updatedScreenState));
      dispatch(receivePool(results));
      dispatch(fetchInfluencerResponseTime(
        results.map((r: CcmOfferResult) => r.creatorProfile.id))
      );

      return results;
    } catch (error) {
      const details = error?.response?.data?.detail;
      const message = details ?? __t(`${MSG_KEY}.errors.generic`);

      logError(error);
      dispatch(danger(message));

      return null;
    }
  }
);

export const generateCcmCampaignTimeline = (
  startDate: string
): ThunkActionCollabary => async (dispatch): Promise<void> => {
  try {
    const url = CCM.generateTimeline(['start-date', startDate]);
    const result = (await get(url))?.data ?? null;

    if (!isCcmCampaignTimeline(result)) {
      throw new Error(`Invalid response for start date: ${startDate}`);
    }

    dispatch(ccmCampaignTimelineGenerated(result));
  } catch (error) {
    logError(error);
  }
};

export const saveCcmCampaignMetadata = (
  campaignId: number,
  metadata: CcmCampaignMetadata
): ThunkActionCollabary => (
  async (dispatch: Dispatch, getState: GetState) => {
    try {
      const url = CCM.saveMetadata(campaignId);

      await patch(url, { data: metadata }, { suppressGlobalNotification: true });

      const updatedCampaign: Campaign = {
        ...getState().campaignBrief.brief,
        ...getState().ccmCampaign,
        salesCockpitNcode: metadata.nCode,
        creativeFactoryInsertionOrderId: metadata.insertionOrderId
      } as Campaign;

      dispatch(receiveBrief(updatedCampaign));
      dispatch(updateCampaign(updatedCampaign));

      return true;
    } catch (error) {
      const errorCode = error?.response?.data?.code;
      const errorMsgByCode = __t(`${MSG_KEY}.errors.${errorCode}`);
      const errorMsg = errorMsgByCode || __t(`${MSG_KEY}.errors.generic`);

      logError(error);
      dispatch(danger(errorMsg));

      return null;
    }
  }
);

export const fetchCcmOutfitPerformance = (
  campaignId: number,
  countries: Array<MixedSelectDropdownItem>
): AsyncAction => (
  async (dispatch: Dispatch) => {
    const countryStrings = countries
      .map(country => country.identifier);

    try {
      const url = CCM.getOutfitPerformance(campaignId, countryStrings.join(','));
      const result = (await get(url))?.data ?? null;

      if (isNil(result)) {
        throw new Error(`Outfit performance data not found for campaign id: ${campaignId}`);
      }

      dispatch(receiveOutfitPerformance(result));

      return result.data;
    } catch (error) {
      const details = error?.response?.data?.detail;
      const message = details ?? __t(`${MSG_KEY}.errors.generic`);

      logError(error);
      dispatch(danger(message));

      return null;
    }
  }
);
